import { getCurrentWebsite, getCurrentWebsiteConfig } from '../../config/websites';

// Supported languages
export type Language = 'en' | 'ar';

// Default language
export const DEFAULT_LANGUAGE: Language = 'en';

// Language direction mapping
export const LANGUAGE_DIRECTION: Record<Language, 'ltr' | 'rtl'> = {
  en: 'ltr',
  ar: 'rtl',
};

// Import default translations directly
import defaultEnTranslations from '../../locales/bluesky/en.json';
import defaultArTranslations from '../../locales/bluesky/ar.json';

// Import website-specific translations
import blueskyEnTranslations from '../../locales/bluesky/en.json';
import blueskyArTranslations from '../../locales/bluesky/ar.json';
import emcorEnTranslations from '../../locales/emcor/en.json';
import emcorArTranslations from '../../locales/emcor/ar.json';
import basraskyEnTranslations from '../../locales/basrasky/en.json';
import basraskyArTranslations from '../../locales/basrasky/ar.json';
import citexEnTranslations from '../../locales/citex/en.json';
import citexArTranslations from '../../locales/citex/ar.json';

// Translation cache
const translationCache = new Map<string, any>([
  ['default-en', defaultEnTranslations],
  ['default-ar', defaultArTranslations],
  ['bluesky-en', blueskyEnTranslations],
  ['bluesky-ar', blueskyArTranslations],
  ['emcor-en', emcorEnTranslations],
  ['emcor-ar', emcorArTranslations],
  ['basrasky-en', basraskyEnTranslations],
  ['basrasky-ar', basraskyArTranslations],
  ['citex-en', citexEnTranslations],
  ['citex-ar', citexArTranslations],
]);

// Load translations from cache
function loadTranslations(language: Language): any {
  try {
    const websiteConfig = getCurrentWebsiteConfig();
    // Use the website key (bluesky/emcor) instead of the display name
    const websiteKey = getCurrentWebsite();
    const cacheKey = `${websiteKey}-${language}`;

    // Try website-specific translations first
    if (translationCache.has(cacheKey)) {
      return translationCache.get(cacheKey);
    }

    // Fallback to default translations
    const defaultKey = `default-${language}`;
    if (translationCache.has(defaultKey)) {
      return translationCache.get(defaultKey);
    }

    return {};
  } catch (error) {
    console.error('Failed to get website config:', error);
    return {};
  }
}

// Current language state
let currentLanguage: Language = DEFAULT_LANGUAGE;
let currentTranslations: any = defaultEnTranslations;

// Initialize i18n
export async function initI18n(language: Language): Promise<void> {
  currentLanguage = language;
  currentTranslations = loadTranslations(language);

  // Set document direction
  if (typeof document !== 'undefined') {
    document.documentElement.dir = LANGUAGE_DIRECTION[language];
    document.documentElement.lang = language;
  }
}

// Get current language
export function getCurrentLanguage(): Language {
  return currentLanguage;
}

// Get translation
export function t(key: string, language?: Language, silent: boolean = false): string {
  const lang = language || currentLanguage;
  const keys = key.split('.');

  // Get translations for the specified language
  const translations = language ? loadTranslations(language) : currentTranslations;

  let value: any = translations;

  for (const k of keys) {
    if (value && typeof value === 'object' && k in value) {
      value = value[k];
    } else {
      // If translation not found, return the key
      if (!silent) {
        console.warn(`Translation key not found: ${key} for language: ${lang}`);
      }
      return key;
    }
  }

  return typeof value === 'string' ? value : key;
}

// Get language direction
export function getLanguageDirection(language?: Language): 'ltr' | 'rtl' {
  const lang = language || currentLanguage;
  return LANGUAGE_DIRECTION[lang];
}

// Check if language is RTL
export function isRTL(language?: Language): boolean {
  return getLanguageDirection(language) === 'rtl';
}

// Get supported languages
export function getSupportedLanguages(): Language[] {
  return Object.keys(LANGUAGE_DIRECTION) as Language[];
}

// Preload all translations
export async function preloadTranslations(): Promise<void> {
  // Translations are already loaded synchronously
  return Promise.resolve();
}

// Initialize with default language immediately, but handle errors gracefully
try {
  initI18n(DEFAULT_LANGUAGE).catch(console.error);
} catch (error) {
  console.warn('Failed to initialize i18n during module load:', error);
}
